import { useEffect, useRef, useState } from 'react';
import { Grain, Thoranam } from '../components/Thoranam';
import { NavBar } from '../components/NavBar';

const VIDEO_ID = 'H8E0H6qO--s';
const WATCH_URL = `https://youtu.be/${VIDEO_ID}`;

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<any> | null = null;

function loadYouTubeApi(): Promise<any> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try {
        prev?.();
      } catch {
        /* ignore */
      }
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    tag.onerror = () => {
      apiPromise = null;
      reject(new Error('Could not load the YouTube player.'));
    };
    document.head.appendChild(tag);
    setTimeout(() => {
      if (!window.YT?.Player) {
        apiPromise = null;
        reject(new Error('Could not load the YouTube player.'));
      }
    }, 15000);
  });
  return apiPromise;
}

export function FilmPage() {
  const mountRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [volume, setVolume] = useState(80);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [castHint, setCastHint] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi()
      .then((YT) => {
        if (cancelled || !mountRef.current) return;
        playerRef.current = new YT.Player(mountRef.current, {
          videoId: VIDEO_ID,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 0,
            controls: 0, // hide the native bar; we render our own minimal bar
            disablekb: 1,
            fs: 0, // hide native fullscreen; our bar has its own button
            iv_load_policy: 3,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
          },
          events: {
            onReady: (e: any) => {
              if (cancelled) return;
              try {
                e.target.setVolume(80);
              } catch {
                /* ignore */
              }
              setVolume(80);
              setMuted(false);
              setReady(true);
            },
            onStateChange: (e: any) => {
              if (cancelled) return;
              const st = e.data;
              const PS = window.YT?.PlayerState;
              setPlaying(PS ? st === PS.PLAYING : st === 1);
              setEnded(PS ? st === PS.ENDED : st === 0);
            },
            onError: () => {
              if (!cancelled) setError('The recording could not be played here. Open it in YouTube instead.');
            },
          },
        });
      })
      .catch(() => {
        if (!cancelled) setError('The recording could not be played here. Open it in YouTube instead.');
      });
    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
      } catch {
        /* ignore */
      }
      playerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const togglePlay = () => {
    const p = playerRef.current;
    if (!p || !ready) return;
    setEnded(false);
    try {
      if (playing) p.pauseVideo();
      else p.playVideo();
    } catch {
      /* ignore */
    }
  };

  const replay = () => {
    const p = playerRef.current;
    if (!p) return;
    setEnded(false);
    try {
      p.seekTo(0, true);
      p.playVideo();
    } catch {
      /* ignore */
    }
  };

  const changeVolume = (v: number) => {
    const p = playerRef.current;
    setVolume(v);
    if (!p) return;
    try {
      if (v === 0) {
        p.mute();
        setMuted(true);
      } else {
        p.unMute();
        p.setVolume(v);
        setMuted(false);
      }
    } catch {
      /* ignore */
    }
  };

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (muted) {
        p.unMute();
        setMuted(false);
      } else {
        p.mute();
        setMuted(true);
      }
    } catch {
      /* ignore */
    }
  };

  const toggleFullscreen = () => {
    const el = frameRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) void document.exitFullscreen();
      else void el.requestFullscreen();
    } catch {
      /* ignore */
    }
  };

  // YouTube's embed offers no per-button toggles, so the Cast control lives in
  // our custom bar and hands off to the YouTube app / site, where the native
  // Cast (and AirPlay) picker is available.
  const cast = () => {
    window.open(WATCH_URL, '_blank', 'noopener');
    setCastHint('Opened in YouTube — use the Cast (or AirPlay) icon there to watch on your TV.');
  };

  return (
    <div className="shell">
      <div className="bg bg-film" />
      <Thoranam />
      <Grain />
      <NavBar />
      <main className="page">
        <header className="page-head">
          <p className="kicker">The ceremony reel</p>
          <h1>Recording</h1>
          <p>The recording of the Shashtipoorthi of Shri Giridhar.</p>
        </header>
        <div className="film-stage">
          <div className="rec-frame" ref={frameRef}>
            <div className="rec-video">
              <div className="rec-screen" ref={mountRef} />
              {/* Veil sits above the chromeless iframe so taps toggle play/pause
                  instead of reaching YouTube's title, logo, or suggestions. */}
              {!ended && (
                <button
                  type="button"
                  className="rec-veil"
                  onClick={togglePlay}
                  aria-label={playing ? 'Pause the recording' : 'Play the recording'}
                >
                  {!playing && (
                    <span className="rec-bigplay" aria-hidden="true">
                      ▶
                    </span>
                  )}
                </button>
              )}
              {ended && (
                <div className="rec-ended">
                  <p>The recording has finished.</p>
                  <button type="button" className="btn gold" onClick={replay}>
                    Play again
                  </button>
                </div>
              )}
            </div>
            <div className="rec-bar" role="toolbar" aria-label="Recording controls">
              <button
                type="button"
                className="rec-btn"
                onClick={togglePlay}
                disabled={!ready}
                aria-label={playing ? 'Pause' : 'Play'}
                title={playing ? 'Pause' : 'Play'}
              >
                <span aria-hidden="true">{playing ? '❚❚' : '▶'}</span>
              </button>
              <div className="rec-vol">
                <button
                  type="button"
                  className="rec-btn"
                  onClick={toggleMute}
                  disabled={!ready}
                  aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}
                  title={muted || volume === 0 ? 'Unmute' : 'Mute'}
                >
                  <span aria-hidden="true">{muted || volume === 0 ? '🔇' : volume < 60 ? '🔉' : '🔊'}</span>
                </button>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={muted ? 0 : volume}
                  onChange={(e) => changeVolume(Number(e.target.value))}
                  disabled={!ready}
                  aria-label="Volume"
                />
              </div>
              <button
                type="button"
                className="rec-btn"
                onClick={cast}
                aria-label="Cast to TV"
                title="Cast to TV"
              >
                <span aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                    <path d="M2 4h20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-6v2h3v2H5v-2h3v-2H2a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm1 2v9h18V6zm2 2v2.5A3.5 3.5 0 0 1 8.5 14H10V12H8.5A1.5 1.5 0 0 0 7 10.5V8z" />
                  </svg>
                </span>
              </button>
              <button
                type="button"
                className="rec-btn"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
                title={isFullscreen ? 'Exit full screen' : 'Full screen'}
              >
                <span aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    {isFullscreen ? (
                      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
                    ) : (
                      <path d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5" />
                    )}
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
        {castHint && <p className="rec-hint">{castHint}</p>}
        {error && (
          <p className="rec-hint">
            {error}{' '}
            <a href={WATCH_URL} target="_blank" rel="noreferrer">
              Open the recording in YouTube
            </a>
          </p>
        )}
      </main>
    </div>
  );
}
